/**
 * eSalon Custom Growth — product recommendation (DEV-4285)
 *
 * A faithful port of the legacy rules in the www repo:
 *   app/Model/Widget.php :: growthProducts()   (called from WidgetController.php:1345)
 *
 * Only TWO of the seven consultation answers affect the result — `health` (scalp
 * concerns) and `loss` (hair loss location). Density, goals, texture, condition
 * and name are collected for personalization and reporting, not for selection.
 * That is the legacy behaviour, not an omission here.
 *
 * Output mirrors the legacy shape: two buckets, `main` (the routine) and `fou`
 * (the shampoo + conditioner pair). Both are lists of internal product KEYS;
 * call toHandles() to resolve them against the Shopify catalog. Keeping the
 * rules and the catalog wiring separate means re-pointing a product is a change
 * to one table rather than to the logic.
 *
 * Registers itself on window.esalonRecommenders under the profile name, so the
 * generic analyzing section can find a recommender without knowing about Growth.
 */

(function attachGrowthRecommendation(global) {
  /** Internal product key -> Shopify product handle. */
  var PRODUCT_HANDLES = {
    minoxidil: 'hair-regrowth-treatment', //          legacy PID 2839
    serum_density: 'densifying-scalp-serum', //       legacy PID 2769
    serum_soothing: 'soothing-scalp-serum', //        legacy PID 2776
    gummies: 'biotin-hair-gummies', //                legacy PID 2790
    vitamins: 'biotin-hair-multivitamins', //         legacy PID 2783
    shampoo_detox: 'scalp-detox-shampoo', //          legacy PID 2797
    shampoo_calm: 'scalp-calming-shampoo', //         legacy PID 2804
    shampoo_repair: 'bond-repair-shampoo', //         legacy PID 2811
    conditioner_repair: 'bond-repair-conditioner', // legacy PID 2818
  };

  /**
   * Scalp concern -> shampoo. In the legacy code this is an if/else-if chain
   * tested against one answer at a time, so the order of the BRANCHES never
   * mattered — only which answer is reached first. A lookup is equivalent.
   */
  var SHAMPOO_BY_CONCERN = {
    flaking: 'shampoo_detox',
    irritation: 'shampoo_calm',
    sensitive: 'shampoo_calm',
    oily: 'shampoo_detox',
    dryness: 'shampoo_repair',
  };

  /** Loss locations that qualify someone for minoxidil. */
  var MINOXIDIL_LOSS = ['general', 'temples', 'part_line'];

  var DEFAULT_SHAMPOO = 'shampoo_repair';

  function toArray(value) {
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return [value];
  }

  /**
   * @param {object} answers - Consultation answers keyed by question (health, loss, ...)
   * @returns {{main: string[], fou: string[]}} product keys
   */
  function recommend(answers) {
    var health = toArray((answers || {}).health);
    var loss = toArray((answers || {}).loss);

    var main = [];
    var fou = [];

    var hasIrritation = health.indexOf('irritation') !== -1;

    // --- Shampoo -----------------------------------------------------------
    // Deliberately order-dependent, matching legacy: the loop stops at the first
    // answer with a rule, so { dryness, flaking } resolves to whichever comes
    // first in the array — there is no priority ranking between the concerns.
    // Confirmed as intended behaviour to preserve rather than a bug to fix.
    // `none` has no rule, so it is skipped and the search continues.
    //
    // What decides that order, precisely: NOT the order the customer clicked.
    // The answers arrive from `getAnswers()`, which reads
    // `querySelectorAll(':checked')` — document order — so the array is always
    // in the option order authored in the page template. Reordering the options
    // in page.growth.json therefore CHANGES which shampoo customers are sent,
    // silently. That is the thing to be careful of, not click sequence.
    var shampoo = null;
    for (var i = 0; i < health.length; i += 1) {
      if (SHAMPOO_BY_CONCERN[health[i]]) {
        shampoo = SHAMPOO_BY_CONCERN[health[i]];
        break;
      }
    }
    fou.push(shampoo || DEFAULT_SHAMPOO);

    // --- Minoxidil ---------------------------------------------------------
    // Irritation suppresses minoxidil outright, whatever the loss answers say.
    // Legacy pushed once per matching loss answer, so selecting `general` AND
    // `temples` duplicated the product; deduped here by decision on DEV-4285.
    var hasMinoxidil = false;
    if (!hasIrritation) {
      for (var j = 0; j < loss.length; j += 1) {
        if (MINOXIDIL_LOSS.indexOf(loss[j]) !== -1) {
          hasMinoxidil = true;
          break;
        }
      }
    }
    if (hasMinoxidil) main.push('minoxidil');

    // --- Serums + supplement -----------------------------------------------
    var hasDensitySerum = false;
    if (!hasMinoxidil) {
      main.push('serum_density');
      hasDensitySerum = true;
    }

    fou.push('conditioner_repair');
    main.push('gummies');

    // Everyone ends up with a density serum; irritation adds the soothing one
    // on top. Reads oddly, but it is exactly the legacy branch: the minoxidil
    // path reaches the else-if with hasDensitySerum still false.
    if (!hasMinoxidil && hasIrritation) {
      main.push('serum_soothing');
    } else if (!hasDensitySerum) {
      main.push('serum_density');
    }

    return { main: main, fou: fou };
  }

  /** Resolve a recommend() result to Shopify product handles. */
  function toHandles(result) {
    function map(keys) {
      return (keys || []).map(function (key) {
        return PRODUCT_HANDLES[key];
      });
    }
    return { main: map(result.main), fou: map(result.fou) };
  }

  global.esalonRecommenders = global.esalonRecommenders || {};
  global.esalonRecommenders.growth = {
    recommend: recommend,
    toHandles: toHandles,
    PRODUCT_HANDLES: PRODUCT_HANDLES,
  };
})(typeof window !== 'undefined' ? window : globalThis);
